import { queryKeys } from "@/shared/api/query-keys";

/**
 * The live channel's vocabulary (`runbooks/events.md` "Live stream (M8)"). Messages carry ids and
 * small deltas only — never a name, never a row — so every handler here does one thing: name the
 * cached queries the change could have touched. The refetch is TanStack's (rules/01 §3).
 */
export const LIVE_EVENT_TYPES = [
  "event.created",
  "device.status",
  "device.updated",
  "person.updated",
  "door.status",
  "sync.updated",
  "tenant.updated",
  "webhook.endpoint.updated",
  // M16 wave 4 (ADR-0021 open question 4): a QR scan at a door, valid or refused. Unlike every other
  // type this one is READ as well as counted — the front desk's live scan view subscribes to the
  // envelopes themselves, because the refusal reason it carries exists in no row to refetch.
  "pass.scan",
  // M27 (contracts §7): an operator calendar's booking moved — `{ resourceId, bookingId, day }`, ids only.
  "rooms.booking_changed",
] as const;

/** `data: {id,type,at,data}` — the envelope the api writes after `id:` and `event:`. */
export interface LiveEnvelope {
  id: string;
  /**
   * One of `LIVE_EVENT_TYPES`, but kept as a plain string on purpose: a newer api may publish a type
   * this build has never heard of, and the mapping below ignores it rather than refusing the frame.
   */
  type: string;
  at: string;
  data: Record<string, unknown>;
}

/** A malformed frame is dropped, never thrown: one bad message must not take the stream down. */
export function parseLiveEnvelope(raw: unknown): LiveEnvelope | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { id, type, at, data } = parsed as Record<string, unknown>;
  if (typeof type !== "string") return null;
  return {
    id: typeof id === "string" ? id : "",
    type,
    at: typeof at === "string" ? at : "",
    data: typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {},
  };
}

function str(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** The Rooms areas `rooms.booking_changed` can touch (the names are `ROOMS_QUERY_AREAS`' in the Rooms module). */
const ROOMS_LIVE_AREAS = ["availability", "bookings", "booking", "overview"] as const;

/**
 * Every prefix the stream can touch, for a connection that opens again with NO id to replay from.
 * A quiet tab (nothing arrived since the page loaded) has no cursor. When the hook reopens after
 * `renew`, or the browser reconnects after a drop, the new connection is a fresh one, and a fresh
 * connection replays nothing (the api's rule, `modules/stream/routes.ts`). Whatever happened in the
 * gap therefore reaches the screen only through one refetch of what the stream would have
 * invalidated (review 2026-10-04). Kept beside `invalidationsFor`: a new message type adds its
 * prefixes here too, and `liveEvents.test.ts` holds the two together.
 */
export function liveCatchUpKeys(tenantId: string): readonly (readonly unknown[])[] {
  const k = queryKeys.tenant;
  return [
    k.events.all(tenantId),
    k.devices.all(tenantId),
    k.doors.all(tenantId),
    k.people.all(tenantId),
    k.settings.all(tenantId),
    k.organisation.all(tenantId),
    k.billing.all(tenantId),
    k.webhooks.all(tenantId),
    k.passes.all(tenantId),
    k.visitors.all(tenantId),
    // M27: the calendar, the lists, the open booking and the overview counts a booking message can move.
    ...ROOMS_LIVE_AREAS.map((area) => k.rooms.area(tenantId, area)),
  ];
}

/**
 * One message → the query keys it invalidates, from the ONE factory (rules/01 §3). Prefixes, not
 * exact keys: `events.all` covers the list, the "N new events" head, the summary and any open row,
 * which is what one ingest batch can move at once.
 */
export function invalidationsFor(tenantId: string, envelope: LiveEnvelope): readonly (readonly unknown[])[] {
  const k = queryKeys.tenant;
  const deviceId = str(envelope.data, "deviceId");
  switch (envelope.type) {
    case "event.created":
      return [k.events.all(tenantId)];
    case "device.status":
      // Presence only: the list (the shell's online count) and that one terminal's page.
      return deviceId
        ? [k.devices.list(tenantId), k.devices.detail(tenantId, deviceId)]
        : [k.devices.all(tenantId)];
    case "device.updated":
      // Pair, unpair, rename, disable: the fleet, its doors, and the usage the plan gates read.
      return [k.devices.all(tenantId), k.doors.all(tenantId), k.settings.all(tenantId)];
    case "person.updated": {
      const change = str(envelope.data, "change");
      const keys: (readonly unknown[])[] = [k.people.all(tenantId)];
      // Someone joined or left the organisation: the people count feeds the plan-limit gates.
      if (change === "created" || change === "deleted") keys.push(k.settings.all(tenantId));
      return keys;
    }
    case "door.status":
      // A remote unlock: the doors page and the terminal page that lists the same door.
      return [k.doors.all(tenantId), k.devices.all(tenantId)];
    case "sync.updated":
      return deviceId
        ? [k.devices.sync(tenantId, deviceId), k.people.all(tenantId)]
        : [k.devices.all(tenantId), k.people.all(tenantId)];
    case "tenant.updated":
      return [k.organisation.all(tenantId), k.settings.all(tenantId), k.billing.all(tenantId)];
    case "webhook.endpoint.updated":
      return [k.webhooks.all(tenantId)];
    case "pass.scan": {
      // A scan that opened a door moved `uses` and `last_used_at` on the pass, and with them the
      // visit's "3 of 5 used". A refusal moved nothing, so it invalidates nothing — the live view
      // renders the message itself. Either way the event log has a new row.
      const passId = str(envelope.data, "passId");
      const keys: (readonly unknown[])[] = [k.events.all(tenantId)];
      if (str(envelope.data, "result") === "opened" && passId) {
        keys.push(k.passes.detail(tenantId, passId), k.visitors.all(tenantId));
      }
      return keys;
    }
    case "rooms.booking_changed": {
      // A booking was made, changed or cancelled: the visible calendar, the lists and counts, and that booking's
      // page if it is open. The message names no person and no title; the refetch goes through the scoped routes.
      const bookingId = str(envelope.data, "bookingId");
      return [
        k.rooms.area(tenantId, "availability"),
        k.rooms.area(tenantId, "bookings"),
        bookingId ? k.rooms.detail(tenantId, "booking", bookingId) : k.rooms.area(tenantId, "booking"),
        k.rooms.area(tenantId, "overview"),
      ];
    }
    default:
      // A type this build does not know yet: ignore it rather than refetching the world.
      return [];
  }
}
